import React, { useState, useRef } from 'react';
import {
  RiUploadCloudLine as UploadIcon,
  RiFileTextLine as FileIcon,
  RiCloseLine as CloseIcon,
  RiCheckLine as CheckIcon,
} from 'react-icons/ri';

export type DocumentType =
  | 'CV_BENEFICIARY'
  | 'CV_REFEREE'
  | 'QUESTIONNAIRE'
  | 'BACKGROUND_INFO'
  | 'PREVIOUS_LETTER'
  | 'SUPPORTING_DOCUMENT'
  | 'OTHER';

export interface FileWithType {
  file: File;
  documentType: DocumentType;
  id: string;
}

interface MultiFileUploadProps {
  onFilesChange: (files: FileWithType[]) => void;
  selectedFiles: FileWithType[];
  disabled?: boolean;
  error?: string;
  maxFiles?: number;
}

const DOCUMENT_TYPE_LABELS: Record<DocumentType, string> = {
  CV_BENEFICIARY: 'Beneficiary CV',
  CV_REFEREE: 'Referee CV',
  QUESTIONNAIRE: 'Questionnaire',
  BACKGROUND_INFO: 'Background Info',
  PREVIOUS_LETTER: 'Previous Letter',
  SUPPORTING_DOCUMENT: 'Supporting Document',
  OTHER: 'Other',
};

const MultiFileUpload: React.FC<MultiFileUploadProps> = ({
  onFilesChange,
  selectedFiles,
  disabled = false,
  error,
  maxFiles = 10,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (disabled) return;

    const files = Array.from(e.dataTransfer.files);
    addFiles(files);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    addFiles(files);
    // Reset input to allow re-selecting same file
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const addFiles = (files: File[]) => {
    const validFiles = files.filter(file => isValidFile(file));

    if (selectedFiles.length + validFiles.length > maxFiles) {
      alert(`Maximum ${maxFiles} files allowed`);
      return;
    }

    const newFilesWithType: FileWithType[] = validFiles.map(file => ({
      file,
      documentType: inferDocumentType(file),
      id: Math.random().toString(36).substring(7),
    }));

    onFilesChange([...selectedFiles, ...newFilesWithType]);
  };

  const isValidFile = (file: File): boolean => {
    const validTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain',
    ];

    if (!validTypes.includes(file.type)) {
      alert(`${file.name}: Please upload a PDF, DOC, DOCX, or TXT file`);
      return false;
    }

    // 200MB limit
    if (file.size > 200 * 1024 * 1024) {
      alert(`${file.name}: File size must be less than 200MB`);
      return false;
    }

    return true;
  };

  const inferDocumentType = (file: File): DocumentType => {
    const fileName = file.name.toLowerCase();

    if (fileName.includes('cv') || fileName.includes('resume')) {
      if (fileName.includes('referee') || fileName.includes('recommender')) {
        return 'CV_REFEREE';
      }
      return 'CV_BENEFICIARY';
    }

    if (fileName.includes('questionnaire') || fileName.includes('question')) {
      return 'QUESTIONNAIRE';
    }

    if (fileName.includes('background') || fileName.includes('bio')) {
      return 'BACKGROUND_INFO';
    }

    if (fileName.includes('letter') || fileName.includes('recommendation')) {
      return 'PREVIOUS_LETTER';
    }

    return 'OTHER';
  };

  const handleRemoveFile = (id: string) => {
    onFilesChange(selectedFiles.filter(f => f.id !== id));
  };

  const handleTypeChange = (id: string, newType: DocumentType) => {
    onFilesChange(
      selectedFiles.map(f =>
        f.id === id ? { ...f, documentType: newType } : f
      )
    );
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-neutral-700">
          Upload Documents
        </label>
        <span className="text-xs text-neutral-500">
          {selectedFiles.length} / {maxFiles} files
        </span>
      </div>

      {/* Upload Area */}
      <div
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => !disabled && fileInputRef.current?.click()}
        className={`
          relative border-2 border-dashed rounded-lg p-6 text-center cursor-pointer
          transition-colors
          ${
            isDragging
              ? 'border-blue-500 bg-blue-50'
              : error
                ? 'border-red-300 bg-red-50'
                : 'border-gray-300 hover:border-blue-400 bg-gray-50'
          }
          ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
        `}
      >
        <input
          ref={fileInputRef}
          type="file"
          onChange={handleFileInputChange}
          accept=".pdf,.doc,.docx,.txt"
          multiple
          className="hidden"
          disabled={disabled}
        />

        <UploadIcon
          className={`w-10 h-10 mx-auto mb-3 ${
            isDragging ? 'text-blue-500' : 'text-gray-400'
          }`}
        />

        <p className="text-base font-medium text-gray-900 mb-1">
          {isDragging
            ? 'Drop files here'
            : 'Click to upload or drag and drop'}
        </p>
        <p className="text-xs text-gray-500">
          PDF, DOC, DOCX, or TXT (max 200MB each)
        </p>
        <p className="text-xs text-gray-500 mt-1">
          CVs, Questionnaires, Background Docs, Previous Letters
        </p>
      </div>

      {error && <p className="text-xs text-red-600 mt-1">{error}</p>}

      {/* File List */}
      {selectedFiles.length > 0 && (
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {selectedFiles.map(fileWithType => (
            <div
              key={fileWithType.id}
              className="border border-neutral-200 rounded-lg p-3 bg-white"
            >
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                  <FileIcon className="h-5 w-5 text-primary-600" />
                </div>

                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-neutral-900 truncate">
                    {fileWithType.file.name}
                  </p>
                  <p className="text-xs text-neutral-500">
                    {formatFileSize(fileWithType.file.size)}
                  </p>

                  {/* Document Type Selector */}
                  <div className="mt-2">
                    <select
                      value={fileWithType.documentType}
                      onChange={e =>
                        handleTypeChange(
                          fileWithType.id,
                          e.target.value as DocumentType
                        )
                      }
                      disabled={disabled}
                      className="text-xs border border-neutral-300 rounded px-2 py-1 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      {Object.entries(DOCUMENT_TYPE_LABELS).map(
                        ([value, label]) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        )
                      )}
                    </select>
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckIcon className="h-4 w-4 text-green-600" />
                  </div>

                  <button
                    type="button"
                    onClick={() => handleRemoveFile(fileWithType.id)}
                    disabled={disabled}
                    className="p-1 text-neutral-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <CloseIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <p className="text-xs text-neutral-500">
        Upload CVs, questionnaires, background documents, and any supporting
        files for letter generation. The system will automatically classify
        document types based on filenames, but you can adjust them manually.
      </p>
    </div>
  );
};

export default MultiFileUpload;
